import {
	useMemo,
	useEffect,
	useRef,
	useCallback,
	RefObject
} from "react";
import {
	InputValidationResult,
	InvalidInputValidationResult,
	IRootValidation,
	ValueValidation
} from "./input";
import {
	CombinedInputValidationResult,
	getCombinedState,
	getStablePathForResultDisconnectedFromValidatorStructure,
	hasAnyValidationResult,
	ImperativeValidationState,
	NodeValidationState
} from "./state";
import {
	executeValidation,
	ExecuteValidationParams,
	SingleInputValidationParams
} from "./validationRunner";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { ConversionError } from "client/Forms/converters";
import { IObjectValidatorBuilder, ROOT_NODE_PATH } from "./builder";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import {
	PathRedirect,
	tryParseServerErrors
} from "./serverErrors";
import {
	EMPTY_ARRAY,
	ensureNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { IValidationStep } from "./steps";
import { ModelPath } from "./path";
import { usePromiseQueue } from "client/Hooks/usePromiseQueue";
import { IValidator } from "./validator";

export function useValidation<TModel>(
	model: TModel,
	validationConfiguration: IObjectValidatorBuilder<TModel>
): IRootValidation<TModel> {
	const [
		inputStates,
		setInputStates,
		getInputStates
	] = useAsyncQueueState<readonly NodeValidationState[]>(EMPTY_ARRAY);

	const triggerQueueRef: RefObject<ExecuteValidationParams[]> = useRef<ExecuteValidationParams[]>(null!);
	const [notifyTriggerQueueIsUpdated, triggerQueueVersion] = useForceRerender();

	const conversionErrorsRef: RefObject<Map<string, ConversionError>> = useRef<Map<string, ConversionError>>(null!);
	const imperativeValidationResultsRef: RefObject<Map<string, ImperativeValidationState>> = useRef<Map<string, ImperativeValidationState>>(null!);
	const lastModelStateForWhichValidationHasRun: RefObject<TModel | null> = useRef<TModel | null>(null);

	if (triggerQueueRef.current === null
		|| conversionErrorsRef.current === null
		|| imperativeValidationResultsRef.current === null
	) {
		triggerQueueRef.current = [];
		conversionErrorsRef.current = new Map();
		imperativeValidationResultsRef.current = new Map();
	}

	const alertManager: AlertManager = useAlerts();

	const rootValidator: IValidator<TModel> = useMemo(
		(): IValidator<TModel> => {
			const result: IValidator<TModel> = validationConfiguration.createNode();

			return result;
		},
		[validationConfiguration]
	);

	const runValidation = useCallback(
		(
			model: TModel,
			params: ExecuteValidationParams,
			signal?: AbortSignal
		): Promise<void> => {
			return setInputStates(
				inputStates => executeValidation(
					model,
					rootValidator,
					params,
					inputStates,
					conversionErrorsRef.current,
					imperativeValidationResultsRef.current,
					signal
				)
			);
		},
		[rootValidator, setInputStates]
	);

	const enqueueValidation = useCallback(
		(params: ExecuteValidationParams): void => {
			triggerQueueRef.current
				.push(params);

			notifyTriggerQueueIsUpdated();
		},
		[notifyTriggerQueueIsUpdated]
	);

	useEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(triggerQueueVersion);
			const triggerQueue: ExecuteValidationParams[] = triggerQueueRef.current;

			const hasSomethingInQueue: boolean = triggerQueue.length > 0;
			const didModelChange: boolean = model !== lastModelStateForWhichValidationHasRun.current;

			const shouldRunValidation: boolean = hasSomethingInQueue || didModelChange;
			if (!shouldRunValidation) {
				return;
			}

			async function validateModel(
				validationParameters: readonly ExecuteValidationParams[],
				signal: AbortSignal
			): Promise<void> {
				try {
					for (const singleParams of validationParameters) {
						await runValidation(
							model,
							singleParams,
							signal
						);

						const paramsIndex: number = triggerQueue.indexOf(singleParams);

						if (paramsIndex !== -1) {
							triggerQueue.splice(paramsIndex, 1);
						}
					}
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			}

			const cancellation = new AbortController();

			const paramsToProcess: readonly ExecuteValidationParams[] = hasSomethingInQueue
				? [...triggerQueue]
				: [{
					action: "input",
					initiatorModelPath: ModelPath.root
				}];

			lastModelStateForWhichValidationHasRun.current = model;

			validateModel(paramsToProcess, cancellation.signal);

			return () => cancellation.abort();
		},
		[
			model,
			runValidation,
			triggerQueueVersion
		]
	);

	const mutateConversionErrorWithoutRerender = useCallback(
		(modelPathForConversionError: ModelPath, conversionError: ConversionError | undefined): void => {
			const matchingStablePaths: readonly string[] = rootValidator.getMatchingStablePaths(
				{
					value: model,
					parentValue: undefined,
					stablePath: ROOT_NODE_PATH,
					modelPath: ModelPath.root
				},
				modelPathForConversionError
			);

			const conversionErrors: Map<string, ConversionError<unknown>> = conversionErrorsRef.current;

			for (const stablePath of matchingStablePaths) {
				if (conversionError) {
					conversionErrors.set(stablePath, conversionError);
				} else {
					conversionErrors.delete(stablePath);
				}
			}
		},
		[model, rootValidator]
	);

	const mutateImperativeValidationResultsWithoutRerender = useCallback(
		(
			modelPaths: Iterable<ModelPath>,
			callback: (state: ImperativeValidationState) => ImperativeValidationState
		): void => {
			const imperativeValidationResults: Map<string, ImperativeValidationState> = imperativeValidationResultsRef.current;

			for (const modelPath of modelPaths) {
				let matchingStablePaths: readonly string[] = rootValidator.getMatchingStablePaths(
					{
						value: model,
						parentValue: undefined,
						stablePath: ROOT_NODE_PATH,
						modelPath: ModelPath.root
					},
					modelPath
				);

				if (matchingStablePaths.length === 0) {
					matchingStablePaths = [
						getStablePathForResultDisconnectedFromValidatorStructure(modelPath)
					];
				}

				for (const stablePath of matchingStablePaths) {
					let currentState: ImperativeValidationState = imperativeValidationResults.get(stablePath)
						?? { modelPath };

					currentState = callback(currentState);

					if (hasAnyValidationResult(currentState)) {
						imperativeValidationResults.set(stablePath, currentState);
					} else {
						imperativeValidationResults.delete(stablePath);
					}
				}
			}
		},
		[model, rootValidator]
	);

	const mutateControlValidationWithoutRerender = useCallback(
		(modelPathForConversionError: ModelPath, controlValidation: InputValidationResult | undefined): void => {
			mutateImperativeValidationResultsWithoutRerender(
				[modelPathForConversionError],
				state => ({
					...state,
					controlValidation
				})
			);
		},
		[mutateImperativeValidationResultsWithoutRerender]
	);

	const serverErrorsHandler = useCallback(
		(serverErrors: ReadonlyMap<ModelPath, InvalidInputValidationResult>): void => {
			const rootValidationError: InvalidInputValidationResult | undefined = serverErrors.get(ModelPath.root);
			if (rootValidationError) {
				alertManager.showError(rootValidationError.errorMessage);
			}

			mutateImperativeValidationResultsWithoutRerender(
				serverErrors.keys(),
				(currentState) => ({
					...currentState,
					serverResult: ensureNotNil(
						serverErrors.get(currentState.modelPath)
					)
				})
			);

			enqueueValidation({
				action: "input",
				initiatorModelPath: ModelPath.root
			});
		},
		[
			alertManager,
			enqueueValidation,
			mutateImperativeValidationResultsWithoutRerender
		]
	);

	const tryProcessServerError = useCallback(
		(
			error: unknown,
			pathRedirects?: readonly PathRedirect[]
		): boolean => {
			const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(error, pathRedirects);
			if (!result.wasSuccessful) {
				return false;
			}

			serverErrorsHandler(result.result);

			return true;
		},
		[serverErrorsHandler]
	);

	const clearServerErrorsHandler = useCallback(
		(modelPaths?: Iterable<ModelPath>): void => {
			const imperativeValidationResults: Map<string, ImperativeValidationState> = imperativeValidationResultsRef.current;
			const modelPathArray: ModelPath[] | undefined = modelPaths
				? [...modelPaths]
				: undefined;

			for (let [stablePath, state] of imperativeValidationResults.entries()) {
				const isInScopeOfPaths: boolean = !modelPathArray
					|| modelPathArray.some(
						p => p.equals(state.modelPath)
							|| p.isAncestorOf(state.modelPath)
					);

				if (isInScopeOfPaths) {
					state = {
						...state,
						serverResult: undefined
					};
				}

				if (hasAnyValidationResult(state)) {
					imperativeValidationResults.set(stablePath, state);
				} else {
					imperativeValidationResults.delete(stablePath);
				}
			}

			enqueueValidation({
				action: "input",
				initiatorModelPath: ModelPath.root
			});
		},
		[enqueueValidation]
	);

	const validateFormHandler = useCallback(
		async (signal?: AbortSignal): Promise<CombinedInputValidationResult> => {
			clearServerErrorsHandler();

			await runValidation(
				model,
				{ action: "formSubmit" },
				signal
			);

			const resultState: readonly NodeValidationState[] = getInputStates();

			return getCombinedState(resultState, ModelPath.root);
		},
		[
			clearServerErrorsHandler,
			getInputStates,
			model,
			runValidation
		]
	);

	const validateStepHandler = useCallback(
		async (
			validationStep: IValidationStep,
			signal?: AbortSignal
		): Promise<CombinedInputValidationResult> => {
			const initiatorModelPaths: ReadonlySet<ModelPath> = validationStep.getPaths();
			clearServerErrorsHandler(initiatorModelPaths);

			await runValidation(
				model,
				{
					action: "stepSubmit",
					initiatorModelPaths
				},
				signal
			);

			const resultStates: readonly NodeValidationState[] = getInputStates();

			return getCombinedState(resultStates, initiatorModelPaths);
		},
		[
			clearServerErrorsHandler,
			getInputStates,
			model,
			runValidation
		]
	);

	const resetHandler = useCallback(
		(): void => {
			setInputStates(() => Promise.resolve([]));
			conversionErrorsRef.current
				.clear();

			imperativeValidationResultsRef.current
				.clear();

			enqueueValidation({
				action: "input",
				initiatorModelPath: ModelPath.root
			});
		},
		[
			enqueueValidation,
			setInputStates
		]
	);

	const getValidationStepStates = useCallback(
		(validationStep: IValidationStep): readonly CombinedInputValidationResult[] => {
			const inputStates: readonly NodeValidationState[] = getInputStates();

			return [
				...validationStep.getPaths()
					.values()
			]
				.map(x => getCombinedState(inputStates, x));
		},
		[getInputStates]
	);

	const rootValidation: ValueValidation<TModel, unknown> = useMemo(
		() => {
			return new ValueValidation<TModel, unknown>(
				ModelPath.root,
				(
					params: SingleInputValidationParams,
					conversionError: ConversionError | undefined,
					controlValidation: InputValidationResult | undefined
				): void => {
					mutateConversionErrorWithoutRerender(params.initiatorModelPath, conversionError);
					mutateControlValidationWithoutRerender(params.initiatorModelPath, controlValidation);
					enqueueValidation(params);
				},
				inputStates,
				(path: ModelPath): void => clearServerErrorsHandler([path])
			);
		},
		[
			clearServerErrorsHandler,
			inputStates,
			mutateConversionErrorWithoutRerender,
			mutateControlValidationWithoutRerender,
			enqueueValidation
		]
	);

	return useMemo(
		(): IRootValidation<TModel> => ({
			validate: validateFormHandler,
			validateStep: validateStepHandler,
			for: (propertyName) => rootValidation.for(propertyName),
			getValidationState: () => rootValidation.getValidationState(),
			setServerErrors: serverErrorsHandler,
			clearServerErrors: (validationStep) => clearServerErrorsHandler(
				validationStep?.getPaths()
			),
			tryProcessServerError: tryProcessServerError,
			triggerInputFinished: (args) => rootValidation.triggerInputFinished(args),
			triggerValueChanged: (args) => rootValidation.triggerValueChanged(args),
			getValidationStepStates,
			isValid: () => rootValidation.isValid(),
			reset: resetHandler
		}),
		[
			clearServerErrorsHandler,
			getValidationStepStates,
			resetHandler,
			rootValidation,
			serverErrorsHandler,
			tryProcessServerError,
			validateFormHandler,
			validateStepHandler
		]
	);
}

function useAsyncQueueState<S>(initialState: S): [
	state: S,
	setState: (callback: (previousState: S) => Promise<S>) => Promise<void>,
	getImmediateState: () => S
] {
	const stateRef: RefObject<S> = useRef<S>(initialState);

	const {
		addTask
	} = usePromiseQueue();

	const [rerender] = useForceRerender();

	const setStateHandler = useCallback(
		(callback: (previousState: S) => Promise<S>): Promise<void> => {
			return addTask(
				async (): Promise<void> => {
					stateRef.current = await callback(stateRef.current);
					rerender();
				}
			);
		},
		[addTask, rerender]
	);

	const getImmediateStateHandler = useCallback(
		(): S => {
			return stateRef.current;
		},
		[]
	);

	return [
		stateRef.current,
		setStateHandler,
		getImmediateStateHandler
	];
}